import React from "react";
import { Link, Link as RouterLink } from "react-router-dom";
import {
    IconButton,
    makeStyles,
    Typography
} from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableContainer
    from "@material-ui/core/TableContainer";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import { Delete, DragHandle as DragHandleIcon, Edit } from "@material-ui/icons";
import styled from "styled-components";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
    usePagination,
    useRowSelect,
    useTable
} from "react-table";
import apiHelper from "../../../../util/ApiHelper";
import { CustomCheckbox, modal } from "../../../../util";
import { useLoading } from "../../../../util/LoadingProvider";
import { toast } from "react-toastify";
import {
    PaginationHandler
} from "../../../../util/useManualPagination";
import dayjs from "dayjs";
import PaginationBarForHandler from "../../../common/PaginationBarForHandler";

const useStyles = makeStyles((theme) => ({
    tabpanel: {
        background: "#FFFFFF",
    },
    textInput: {
        backgroundColor: "white",
        width: "100%",
        marginBottom: "12px",

        "& input": {
            fontSize: "14px",
        },
    },
    content: {
        "& .ck-editor__editable_inline": {
            minHeight: "200px",
        },
    },
    colLink: {
        textDecoration: "none",
        color: "#306F9D"
    },
    buttonGroup: {
        marginTop: "20px",
        textAlign: "right",
        "& button": {
            color: "#306F9D",
            fontSize: "13px",
            textTransform: "inherit",
        },
    },
    box: {
        padding: 0,
        background: "#ffffff",
        paddingTop: "10px",
        paddingBottom: "10px",
        minHeight: "800px",
    },
    innerBox: {
        background: "#ffffff",
        padding: theme.spacing(3),
        marginBottom: theme.spacing(4),
    },
    button: {
        backgroundColor: "#306F9D",
        textTransform: "capitalize",
        color: "white",
        "&:hover": {
            backgroundColor: "#306F9D",
        },
    },
    pagination: {
        "& .MuiTextField-root": {
            width: "35px",
            minWidth: "auto",
        },
        "& .MuiSelect-root": {
            paddingTop: "3px",
            paddingBottom: "3px",
        },
        "& input": {
            padding: "4px 0",
            marginLeft: "10px",
            marginRight: "10px",
        },
        "& button": {
            background: "white",
            color: "#cccccc",
            border: "1px solid #CCCCCC",
            minWidth: "auto",
            width: "24px",
            height: "24px",
            margin: "4px 0",
            "&.left": {
                borderRadius: "4px 0 0 4px",
                marginLeft: "10px",
            },
            "&.right": {
                borderRadius: " 0 4px 4px 0",
            },
        },
    },
}));

const statuses = {
    1: "active",
    2: "inactive",
};

const IndeterminateCheckbox = React.forwardRef(
    ({ indeterminate, ...rest }, ref) => {
        const defaultRef = React.useRef();
        const resolvedRef = ref || defaultRef;

        React.useEffect(() => {
            resolvedRef.current.indeterminate = indeterminate;
        }, [resolvedRef, indeterminate]);

        return <CustomCheckbox type="checkbox"
            ref={resolvedRef} {...rest} />;
    }
);

const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
};

class DraggableTableCell extends React.Component {
    getSnapshotBeforeUpdate(prevProps) {
        if (!this.ref) return null;
        const isDragStarting =
            this.props.isDragOccurring && !prevProps.isDragOccurring;
        if (!isDragStarting) return null;
        const { width, height } = this.ref.getBoundingClientRect();
        return { width, height };
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        const ref = this.ref;
        if (!ref) return;
        if (snapshot) {
            if (ref.style.width === snapshot.width) return;
            ref.style.width = `${snapshot.width}px`;
            ref.style.height = `${snapshot.height}px`;
            return;
        }
        if (this.props.isDragOccurring) return;
        if (ref.style.width == null) return;
        ref.style.removeProperty("height");
        ref.style.removeProperty("width");
    }

    setRef = (ref) => {
        this.ref = ref;
    };

    render() {
        return (
            <TableCell
                style={{ paddingTop: 6, paddingBottom: 6, lineHeight: "1.1em", height: "48px" }}
                ref={this.setRef}
                align={this.props.align}
                className={this.props.className}
            >
                {this.props.children}
            </TableCell>
        );
    }
}

const DraggableTableRow = ({
    snapshot,
    row,
    provided,
    orderingDisabled,
    isDragging,
}) => (
    <TableRow
        ref={provided.innerRef}
        {...provided.draggableProps}
        style={{
            ...provided.draggableProps.style,
            background: snapshot.isDragging ? "#f5f5f5" : "white",
        }}
    >
        {row.cells.map((cell, index) =>
            cell.column.id !== "dragHandle" ? (
                <DraggableTableCell
                    key={cell.column.id}
                    isDragOccurring={isDragging}
                    className={cell.column.className}
                >
                    {cell.render("Cell")}
                </DraggableTableCell>
            ) : null
        )}
        <TableCell
            style={{
                paddingTop: 6,
                paddingBottom: 6,
                lineHeight: "1.1em",
                height: "48px",
                color: "#CCCCCC",
                cursor: "grab",
            }}
            {...(!orderingDisabled && { ...provided.dragHandleProps })}
        >
            <DragHandleIcon />
        </TableCell>
    </TableRow>
);

const StatusFilter = styled.ul`
  list-style: none;
  display: inline-flex;
  padding: 0;
  margin-left: 25px;

  li {
    button {
      border: 0px;
      background: transparent;
      color: #306f9d;
      font-size: 13px;
      line-height: 15px;
      padding: 0 10px;
      cursor: pointer;

      &.active {
        font-weight: bold;
        color: #333333;
      }
    }

    &:not(:last-of-type) {
      button {
        border-right: 1px solid black;
      }
    }

    &:first-of-type {
      button {
        padding-left: 0;
      }
    }
  }
`;

const formatDatetime = (dt) => {
    return dayjs(dt).format("DD MMM YYYY, hh:mma");
};

function DistrictListingPage(props) {
    const { match, path } = props;
    const classes = useStyles();
    const [disabled, setDisabled] = React.useState(props.disabled);
    const [actionType, setActionType] = React.useState("");
    const [keyword, setKeyword] = React.useState("");
    const [queryString, setQueryString] = React.useState("");
    const [data, setData] = React.useState([]);
    const [totalPages, setTotalPages] = React.useState(1);
    const [status, setStatus] = React.useState(null);
    const [paginationHandler] = React.useState(new PaginationHandler());
    const { setLoading } = useLoading();
    const [isDragging, setIsDragging] = React.useState(false);

    // for pagination
    const [elementCounts, setElementCounts] = React.useState({
        all: 0,
        active: 0,
        inactive: 0,
    });

    const CustomLink = React.useMemo(
        () =>
            React.forwardRef((linkProps, ref) => <Link
                ref={ref} {...linkProps} />),
        []
    );

    const handleSelectChange = (e) => {
        setActionType(e.target.value);
    };


    const columns = React.useMemo(
        () => [
            {
                Header: "District Name",
                accessor: "localizations.en.name",
                Cell: ({ row }) => {
                    return (
                        <Link
                            to={`/hkiff/region-management/district/${row.original.id}`}
                            className={classes.colLink}
                        >
                            {String(row.original.localizations.en.name)}
                        </Link>
                    );
                },
            },
            {
                Header: "Region",
                accessor: "localizations.region",
                Cell: ({ row }) => {
                    return (
                        <Link
                            to={`/hkiff/region-management/district/${row.original.id}`}
                            className={classes.colLink}
                        >
                            {String(row.original.region)}
                        </Link>
                    );
                },
            },
            {
                Header: "Status",
                accessor: "status.status",
                Cell: ({ row }) => {
                    return (
                        <Link
                            to={`/hkiff/region-management/district/${row.original.id}`}
                            className={classes.colLink}
                        >
                            {row.original.status.status === true ? "Active" : "Inactive"}
                        </Link>
                    );
                },
            },
            {
                Header: "Last Update",
                accessor: "updateTime",
                Cell: ({ row }) => {
                    return formatDatetime(row.original.updateTime);
                },
            },
        ],
        [disabled]
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        setPageSize,
        gotoPage,
        previousPage,
        nextPage,
        canPreviousPage,
        canNextPage,
        selectedFlatRows,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data,
            initialState: { pageIndex: 0, pageSize: 10 },
            manualPagination: true,
            pageCount: totalPages,
        },
        usePagination,
        useRowSelect,
        (hooks) => {
            hooks.visibleColumns.push((columns) => [
                // Let's make a column for selection
                {
                    id: "selection",
                    // The header can use the table's getToggleAllRowsSelectedProps method
                    // to render a checkbox
                    Header: ({ getToggleAllRowsSelectedProps }) => (
                        <div>
                            <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
                        </div>
                    ),
                    // The cell can use the individual row's getToggleRowSelectedProps method
                    // to the render a checkbox
                    Cell: ({ row }) => (
                        <div>
                            <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
                        </div>
                    ),
                },
                ...columns,
                {
                    id: "dragHandle",
                    Header: () => <></>,
                },
                {
                    id: "action",
                    // The header can use the table's getToggleAllRowsSelectedProps method
                    // to render a checkbox
                    Header: () => <div>Action</div>,
                    // The cell can use the individual row's getToggleRowSelectedProps method
                    // to the render a checkbox
                    Cell: ({ row }) => (
                        <div
                            style={{ display: "inline-flex" }}>
                            <IconButton
                                className={classes.iconBtn}
                                size="small"
                                component={CustomLink}
                                to={`/hkiff/region-management/district/${row.original.id}`}
                            >
                                <Edit fontSize="small" />
                            </IconButton>
                            {disabled || (
                                <IconButton
                                    className={classes.iconBtn}
                                    size="small"
                                    onClick={() => {
                                        deleteDistrict(row.original.id);
                                    }}
                                >
                                    <Delete
                                        fontSize="inherit" />
                                </IconButton>
                            )}
                        </div>
                    ),
                },
            ]);
        }
    );

    const getDistrict = React.useCallback(async () => {
        try {
            setLoading(true);
            const regions = await apiHelper.hkiff.getAllRegions();
            const result = await apiHelper.hkiff.listDistrict(
                "en",
                0,
                0,
                999999
            );
            result.data.content.map((x)=>{
                x.region = "";
                if(x.regionId != null) {
                    var region = regions.data.find(r => r.id === x.regionId);
                    if(typeof region != "undefined"){
                        x.region = region.name;
                    }
                }
            });
            paginationHandler.setPageIndex(0).setRows(result.data.content);
            const content = paginationHandler.filterRowsByAll().getRows();
            const totalPages = paginationHandler.getTotalPages();
            const all = paginationHandler.getTotalRows();
            const active = paginationHandler.getStatuslessRows()
                .filter((x) => x && x.status.status === true).length;
            const inactive = paginationHandler.getStatuslessRows()
                .filter((x) => x && x.status.status === false).length;
                
            setElementCounts({
                all,
                active,
                inactive,
            });
            setData(content.filter((x) => x));
            setTotalPages(totalPages);
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }, [paginationHandler, setLoading]);

    React.useEffect(() => {
        if (paginationHandler) getDistrict(); // only get when paginationHandler ready
    }, [getDistrict, paginationHandler]);

    const statusFilterHandler = (targetStatus) => () => {
        setLoading(true);
        setStatus(targetStatus);
        let target;
        if (targetStatus === statuses[1]) {
            target = true;
        } else if (targetStatus === statuses[2]) {
            target = false;
        } else {
            target = null;
        }
        paginationHandler
            .resetRows()
            .setQueryStatus("status.status", target)
            .filterRowsByAll();
        console.log(paginationHandler.getRows());
        setData(paginationHandler.getRows());
        gotoPage(0);
        setLoading(false);
    };

    const triggerSearch = React.useCallback(() => {
        setLoading(true);
        setQueryString(keyword);
        paginationHandler
            .resetRows()
            .setQueryString(["localizations.en.name", "localizations.tc.name", "region"], keyword)
            .filterRowsByAll();
        setData(paginationHandler.getRows());
        console.log(paginationHandler.getRows());
        const all = paginationHandler.getStatuslessRows().length;
        const active = paginationHandler
            .getStatuslessRows()
            .filter((x) => x.status.status == true).length;
        const inactive = paginationHandler
            .getStatuslessRows()
            .filter((x) => x.status.status == false).length;
        setElementCounts({
            all,
            active,
            inactive,
        });
        gotoPage(0);
        setLoading(false);
    }, [gotoPage, keyword, paginationHandler, setLoading]);

    const orderingDisabled = status !== null;

    const onBeforeDragStart = React.useCallback(() => {
        setIsDragging(true);
    }, []);

    const onDragEnd = React.useCallback(
        async (result) => {
            setIsDragging(false);
            if (!result.destination) return;
            if (orderingDisabled) return;

            const fullList = paginationHandler.getAllRows();
            if (fullList.length === 0) return;

            const startIndex = pageIndex * pageSize + result.source.index;
            const endIndex = pageIndex * pageSize + result.destination.index;
            const newOrderedList = reorder(fullList, startIndex, endIndex);
            const bulkUpdateSeqRequest = newOrderedList.reduce(
                (acc, item, idx) => ({ ...acc, [item.id]: idx }),
                {}
            );

            try {
                setLoading(true);
                const res = await apiHelper.hkiff.updateDistrictSequence(bulkUpdateSeqRequest);
                if (res.data?.code === 0) {
                    toast.dark("District order updated.");
                    await getDistrict();
                } else {
                    toast.error("Failed to update order.");
                }
            } catch (err) {
                console.error(err);
                toast.error("An error occurred while updating order.");
            } finally {
                setLoading(false);
            }
        },
        [pageIndex, pageSize, paginationHandler, getDistrict, setLoading, orderingDisabled]
    );

    const deleteDistrict = React.useCallback(async (id) => {
        const decision = await modal.fire({
            title: "Delete District",
            text: "Are you sure to delete this district?",
            showCancelButton: true,
            confirmButtonText: `Delete`,
        });
        if (decision.isConfirmed) {
            try {
                const result = await apiHelper.hkiff.deleteDistrictById(id);
                if (result.data.code === 0) {
                    toast.dark("District deleted.");
                }
                await getDistrict();
            } catch (err) {
                console.log(err);
            }
        }
    }, [rows, actionType, getDistrict]);

    const handleBulkAction = React.useCallback(async () => {
        switch (actionType) {
            case "DELETE":
                console.log(selectedFlatRows);
                const ids = selectedFlatRows.map((o) => o.original.id);
                console.log(ids);
                console.log(data);
                if (ids.length > 0) {
                    const decision = await modal.fire({
                        title: "Delete District",
                        text: `Are you sure to delete ${ids.length} district(s)?`,
                        showCancelButton: true,
                        confirmButtonText: `Delete`,
                    });
                    if (decision.isConfirmed) {
                        try {
                            const result = await apiHelper.hkiff.bulkDeleteDistrict(ids);
                            if (result.data.code === 0) {
                                toast.dark(`${ids.length} district deleted.`);
                            }
                            await getDistrict();
                        } catch (err) {
                            console.log(err);
                        }
                    }
                }
                break;
            default:
                break;
        }
    }, [rows, actionType, getDistrict]);

    return (
        <>
            <StatusFilter>
                <li>
                    <button
                        className={status === null ? "active" : ""}
                        type="button"
                        onClick={statusFilterHandler(null)}
                    >
                        <Typography variant="p">All
                                    ({elementCounts.all})</Typography>
                    </button>
                </li>
                <li>
                    <button
                        className={status === "active" ? "active" : ""}
                        type="button"
                        onClick={statusFilterHandler(statuses[1])}
                    >
                        <Typography variant="p">
                            Active
                                    ({elementCounts.active})
                                </Typography>
                    </button>
                </li>
                <li>
                    <button
                        className={status === "inactive" ? "active" : ""}
                        type="button"
                        onClick={statusFilterHandler(statuses[2])}
                    >
                        <Typography variant="p">
                            Inactive
                                    ({elementCounts.inactive})
                                </Typography>
                    </button>
                </li>
                {String(selectedFlatRows)}
            </StatusFilter>
            <Grid
                container
                justify="space-between"
                style={{ padding: "0 25px 10px" }}
            >
                <Grid container item xs={3} md={3}>
                    <Box width="100%"
                        component="span"
                        display="flex">
                        <Box width="100%">
                            <FormControl fullWidth
                                margin="dense"
                                variant="outlined">
                                <InputLabel
                                    id="demo-simple-select-outlined-label">
                                    Bulk Actions
                                        </InputLabel>
                                <Select
                                    labelId="demo-simple-select-outlined-label"
                                    id="demo-simple-select-outlined"
                                    value={actionType}
                                    onChange={handleSelectChange}
                                    label="Bulk Actions"
                                >
                                    <MenuItem
                                        value="">
                                        <em>None</em>
                                    </MenuItem>
                                    <MenuItem
                                        value={"DELETE"}>Delete</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                        <Box display="flex">
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={handleBulkAction}
                                disabled={disabled}
                            >
                                Apply
                                    </Button>
                        </Box>
                    </Box>
                </Grid>
                <Grid container item xs={12} md={8}>
                    <Box
                        width="100%"
                        component="span"
                        display="flex"
                        justifyContent="flex-end"
                        alignItems="center"
                    >
                        <Box display="flex">
                            <Button
                                variant="contained"
                                color="primary"
                                type="button"
                                component={RouterLink}
                                disabled={disabled}
                                to={`${path}/new`}
                            >
                                Add New District
                                    </Button>
                        </Box>
                        <Box display="flex">
                            <TextField
                                id="outlined-error"
                                label="Search"
                                variant="outlined"
                                margin="dense"
                                value={keyword}
                                onChange={(e) => setKeyword(e.target.value)}
                            />
                        </Box>
                        <Box display="flex">
                            <Button
                                variant="outlined"
                                color="primary"
                                type="button"
                                margin="dense"
                                onClick={triggerSearch}
                            >
                                Search
                                    </Button>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
            <Grid container xs={12}>
                <TableContainer
                    component={Paper}
                    style={{
                        margin: "0 25px",
                        boxShadow: "none",
                        border: "1px solid #cccccc",
                    }}
                >
                    <fieldset disabled={disabled}
                        className="fieldset">
                        <Table
                            aria-label="simple table"
                            component="ul"
                            style={{ margin: "0" }}
                            {...getTableProps()}
                        >
                            <TableHead
                                component="div">
                                {headerGroups.map((headerGroup) => (
                                    <TableRow {...headerGroup.getHeaderGroupProps()}>
                                        {headerGroup.headers.map((column) => (
                                            <TableCell
                                                {...column.getHeaderProps()}
                                                className={`${column.className ?? ""}`}
                                                style={{
                                                    paddingTop: 6,
                                                    paddingBottom: 6,
                                                    lineHeight: "1.1em",
                                                    height: "48px",
                                                }}
                                            >
                                                {column.render("Header")}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableHead>
                            <DragDropContext
                                onBeforeDragStart={onBeforeDragStart}
                                onDragEnd={onDragEnd}
                            >
                                <Droppable droppableId="district-droppable">
                                    {(provided) => (
                                        <TableBody
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                            {...getTableBodyProps()}
                                        >
                                            {rows.map((row, index) => {
                                                prepareRow(row);
                                                return (
                                                    <Draggable
                                                        key={row.original.id}
                                                        draggableId={String(row.original.id)}
                                                        index={index}
                                                        isDragDisabled={disabled || orderingDisabled}
                                                    >
                                                        {(prov, snap) => (
                                                            <DraggableTableRow
                                                                row={row}
                                                                provided={prov}
                                                                snapshot={snap}
                                                                orderingDisabled={disabled || orderingDisabled}
                                                                isDragging={isDragging}
                                                            />
                                                        )}
                                                    </Draggable>
                                                );
                                            })}
                                            {provided.placeholder}
                                        </TableBody>
                                    )}
                                </Droppable>
                            </DragDropContext>
                        </Table>
                    </fieldset>
                </TableContainer>
            </Grid>
            <Grid
                container
                justify="flex-end"
                style={{
                    paddingRight: "25px",
                    marginTop: "15px"
                }}
            >
                <div className={classes.flexRow}>
                    <div></div>
                    <PaginationBarForHandler
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        setData={setData}
                        setTotalPages={setTotalPages}
                        paginationHandler={paginationHandler}
                        gotoPage={gotoPage}
                        pageIndex={pageIndex}
                        canPreviousPage={canPreviousPage}
                        previousPage={previousPage}
                        nextPage={nextPage}
                        canNextPage={canNextPage}
                        elementCounts={elementCounts}
                        status={status}
                    />
                </div>
            </Grid>
        </>
    );
}

export default DistrictListingPage;
